import { useEffect, useRef, useState } from 'react';

// Extend window object to include YT namespace
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

// YouTube Player types
interface YT {
  Player: new (elementId: string, config: PlayerConfig) => Player;
  PlayerState: {
    UNSTARTED: -1;
    ENDED: 0;
    PLAYING: 1;
    PAUSED: 2;
    BUFFERING: 3;
    CUED: 5;
  };
}

interface PlayerConfig {
  height?: string | number;
  width?: string | number;
  videoId: string;
  playerVars?: {
    autoplay?: 0 | 1;
    controls?: 0 | 1;
    modestbranding?: 0 | 1;
    rel?: 0 | 1;
    showinfo?: 0 | 1;
    fs?: 0 | 1;
    start?: number;
    end?: number;
    enablejsapi?: 0 | 1;
  };
  events?: {
    onReady?: (event: PlayerEvent) => void;
    onStateChange?: (event: PlayerEvent) => void;
    onError?: (event: PlayerEvent) => void;
  };
}

interface PlayerEvent {
  target: Player;
  data?: number;
}

interface Player {
  playVideo: () => void;
  pauseVideo: () => void;
  stopVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  setVolume: (volume: number) => void;
  getVolume: () => number;
  mute: () => void;
  unMute: () => void;
  isMuted: () => boolean;
  destroy: () => void;
  loadVideoById: (videoId: string, startSeconds?: number) => void;
}

interface UseYouTubePlayerOptions {
  videoId: string;
  autoplay?: boolean;
  controls?: boolean;
  width?: string | number;
  height?: string | number;
  startSeconds?: number;
  onReady?: (player: Player) => void;
  onStateChange?: (event: PlayerEvent) => void;
  onError?: (event: PlayerEvent) => void;
}

interface UseYouTubePlayerReturn {
  playerRef: React.RefObject<HTMLDivElement>;
  player: Player | null;
  isReady: boolean;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  play: () => void;
  pause: () => void;
  seekTo: (seconds: number) => void;
  setVolume: (volume: number) => void;
  loadVideo: (videoId: string, startSeconds?: number) => void;
}

// Global flag to track if API is loaded
let isAPILoaded = false;
let isAPILoading = false;
const apiLoadCallbacks: (() => void)[] = [];

function loadYouTubeAPI(): Promise<void> {
  return new Promise((resolve) => {
    if (isAPILoaded && window.YT) {
      resolve();
      return;
    }

    // Add callback to queue
    apiLoadCallbacks.push(resolve);

    // If already loading, don't load again
    if (isAPILoading) {
      return;
    }

    isAPILoading = true;

    // Create script tag
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

    // Set up callback
    window.onYouTubeIframeAPIReady = () => {
      isAPILoaded = true;
      isAPILoading = false;
      // Resolve all waiting callbacks
      apiLoadCallbacks.forEach((callback) => callback());
      apiLoadCallbacks.length = 0;
    };
  });
}

export function useYouTubePlayer(
  options: UseYouTubePlayerOptions
): UseYouTubePlayerReturn {
  const {
    videoId,
    autoplay = false,
    controls = true,
    width = '100%',
    height = '100%',
    startSeconds = 0,
    onReady,
    onStateChange,
    onError
  } = options;

  const playerRef = useRef<HTMLDivElement>(null);
  const playerInstanceRef = useRef<Player | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Load YouTube API and create player
  useEffect(() => {
    if (!videoId || !playerRef.current) return;

    let player: Player | null = null;
    let updateInterval: NodeJS.Timeout | null = null;

    const initializePlayer = async () => {
      await loadYouTubeAPI();

      if (!playerRef.current || playerInstanceRef.current) return;

      // Generate unique ID for player div
      const playerId = `youtube-player-${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      playerRef.current.id = playerId;

      player = new window.YT.Player(playerId, {
        height,
        width,
        videoId,
        playerVars: {
          autoplay: autoplay ? 1 : 0,
          controls: controls ? 1 : 0,
          modestbranding: 1,
          rel: 0,
          enablejsapi: 1,
          start: startSeconds
        },
        events: {
          onReady: (event: any) => {
            setIsReady(true);
            setDuration(event.target.getDuration());
            playerInstanceRef.current = event.target;
            onReady?.(event.target);

            // Start time update interval
            updateInterval = setInterval(() => {
              if (playerInstanceRef.current) {
                const time = playerInstanceRef.current.getCurrentTime();
                setCurrentTime(time);
              }
            }, 100);
          },
          onStateChange: (event: any) => {
            const state = event.data;
            setIsPlaying(state === window.YT.PlayerState.PLAYING);
            onStateChange?.(event);
          },
          onError: (event: any) => {
            console.error('YouTube Player Error:', event);
            onError?.(event);
          }
        }
      });
    };

    initializePlayer();

    return () => {
      if (updateInterval) {
        clearInterval(updateInterval);
      }
      if (playerInstanceRef.current) {
        try {
          playerInstanceRef.current.destroy();
        } catch (error) {
          console.error('Error destroying YouTube player:', error);
        }
        playerInstanceRef.current = null;
      }
      setIsReady(false);
    };
  }, [videoId, autoplay, controls, width, height, startSeconds]);

  // Control functions
  const play = () => {
    playerInstanceRef.current?.playVideo();
  };

  const pause = () => {
    playerInstanceRef.current?.pauseVideo();
  };

  const seekTo = (seconds: number) => {
    playerInstanceRef.current?.seekTo(seconds, true);
  };

  const setVolume = (volume: number) => {
    playerInstanceRef.current?.setVolume(Math.max(0, Math.min(100, volume)));
  };

  const loadVideo = (newVideoId: string, newStartSeconds = 0) => {
    playerInstanceRef.current?.loadVideoById(newVideoId, newStartSeconds);
  };

  return {
    playerRef,
    player: playerInstanceRef.current,
    isReady,
    isPlaying,
    currentTime,
    duration,
    play,
    pause,
    seekTo,
    setVolume,
    loadVideo
  };
}
